"use client";

/**
 * Candidate-level "Selected Email Address" control (Figma `10356:11104`).
 *
 * Shows the address the enrollment will send to and lets the recruiter:
 *   - pick a different on-file address from the dropdown,
 *   - add a new address via the shared Contact Information modal (the same
 *     one the pipeline / enrollments tables use). After they add an email
 *     and close the modal, a single on-file email auto-selects; otherwise
 *     they reopen the dropdown and pick the new (Manual) one.
 *
 * Persistence: the chosen address is written as the per-step
 * `email_to_address` override (see `step-pane`). The backend send path
 * honours that frozen address ahead of the waterfall pick, and the
 * validator clears `contact_email_missing` once any override email is
 * present — so selecting (or adding) an address resolves the blocking
 * "no deliverable email" fix issue.
 */

import * as React from "react";

import { Icon } from "@/components/ui/icon";
import { Select } from "@/components/ui/select";
import { ContactReviewModal } from "@/features/candidate/components/profile/contact/actions/review-modal";
import {
  ContactItemDeliverability,
  type ContactDeliverability,
} from "@/features/candidate/components/profile/contact/item/deliverability";
import { useProfileContacts } from "@/hooks/use-contact-reveal";

export type EmailType = "personal" | "professional" | null;

export interface EmailOption {
  address: string;
  emailType: EmailType;
  isManual?: boolean;
  /** Raw deliverability from the contact provider (e.g. "deliverable",
   * "risk_elevated", "undeliverable", "unknown", null when unverified). */
  deliverability?: string | null;
}

export interface EmailSelectorProps {
  profileId: string;
  /** Currently-selected recipient address (override or waterfall pick). */
  selectedEmail: string | null;
  selectedEmailType: EmailType;
  /** Pass `null` to clear the recipient (reverts to the waterfall pick). */
  onSelect: (option: EmailOption | null) => void;
}

function typeLabel(option: { emailType: EmailType; isManual?: boolean }):
  | string
  | null {
  if (option.isManual) return "Manual";
  if (option.emailType === "personal") return "Personal";
  if (option.emailType === "professional") return "Professional";
  return null;
}

/** Amber type pill (matches the Figma badge for the address type). */
function TypePill({ label }: { label: string }) {
  return (
    <span className="inline-flex shrink-0 items-center rounded-md bg-[#f6a951]/[0.06] px-1.5 py-0.5">
      <span className="text-xs text-[#ffc787]">{label}</span>
    </span>
  );
}

function isUndeliverable(value: string | null | undefined): boolean {
  return (value ?? "").toLowerCase() === "undeliverable";
}

/** Risky + undeliverable are never auto-selected (mirrors the backend
 * waterfall pool). They stay manually selectable in the dropdown. */
function isAutoSelectable(value: string | null | undefined): boolean {
  const v = (value ?? "").toLowerCase();
  return v !== "undeliverable" && v !== "risk_elevated" && v !== "risky";
}

export function EmailSelector({
  profileId,
  selectedEmail,
  selectedEmailType,
  onSelect,
}: EmailSelectorProps) {
  const [open, setOpen] = React.useState(false);
  const [contactModalOpen, setContactModalOpen] = React.useState(false);
  // Snapshot of known email addresses when the contact modal opened, so we
  // can detect a freshly-added one and auto-select when it's the only one.
  const knownBeforeAddRef = React.useRef<string[] | null>(null);

  const contactsQuery = useProfileContacts(profileId);

  // Real on-file email addresses (lower-cased) — the authoritative set the
  // backend waterfall draws from. Distinct from `options`, which may also
  // include a synthetic entry for an off-file selected address.
  const onFileAddresses = React.useMemo<Set<string>>(() => {
    const s = new Set<string>();
    for (const c of contactsQuery.data?.contacts ?? []) {
      if (c.type === "email" && c.value) s.add(c.value.toLowerCase());
    }
    return s;
  }, [contactsQuery.data]);

  const options = React.useMemo<EmailOption[]>(() => {
    const out: EmailOption[] = [];
    const seen = new Set<string>();
    for (const c of contactsQuery.data?.contacts ?? []) {
      if (c.type !== "email" || !c.value) continue;
      const key = c.value.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({
        address: c.value,
        emailType: (c.emailType ?? null) as EmailType,
        isManual: Boolean(c.isLocal),
        deliverability: c.deliverability ?? null,
      });
    }
    if (selectedEmail && !seen.has(selectedEmail.toLowerCase())) {
      out.unshift({ address: selectedEmail, emailType: selectedEmailType });
    }
    return out;
  }, [contactsQuery.data, selectedEmail, selectedEmailType]);

  // Track which addresses we've positively observed on file for THIS
  // profile. Auto-revert (below) only fires for an address we saw on file
  // and that was then removed — so a backend waterfall pick we simply have
  // no matching on-file row for is never wrongly cleared. Reset on profile
  // change so a coincidental address match across candidates can't leak.
  const everOnFileRef = React.useRef<Set<string>>(new Set());
  React.useEffect(() => {
    everOnFileRef.current = new Set();
  }, [profileId]);
  React.useEffect(() => {
    onFileAddresses.forEach((a) => everOnFileRef.current.add(a));
  }, [onFileAddresses]);

  // After the contact modal closes, reconcile: once the contacts list has
  // settled (the add mutation's refetch finished), auto-select the freshly
  // added address — or the sole on-file email — per the spec.
  React.useEffect(() => {
    if (contactModalOpen) return;
    const before = knownBeforeAddRef.current;
    if (!before) return;
    // Wait for the refetch so a just-added address is actually present.
    if (contactsQuery.isFetching) return;
    knownBeforeAddRef.current = null;
    // Only auto-select among addresses the waterfall would consider
    // (deliverable / unverified) — never risky or undeliverable.
    const emails = options.filter((o) => isAutoSelectable(o.deliverability));
    if (emails.length === 0) return;
    const added = emails.filter(
      (o) => !before.includes(o.address.toLowerCase()),
    );
    // Prefer the newly-added address; otherwise fall back to the sole
    // on-file email.
    if (added.length === 1) {
      onSelect(added[0]!);
    } else if (emails.length === 1 && !selectedEmail) {
      onSelect(emails[0]!);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reconcile on settle
  }, [contactModalOpen, options, contactsQuery.isFetching]);

  // If the chosen address is deleted from the candidate's contacts, clear
  // the selection so the recipient reverts to the waterfall pick — and the
  // recipient drops back into the Fix bucket when nothing deliverable
  // remains. Conservative: only revert an address we saw on file and that
  // is now gone (never a backend pick with no matching on-file row).
  React.useEffect(() => {
    if (contactModalOpen) return;
    if (knownBeforeAddRef.current) return; // add-reconcile owns selection
    if (!selectedEmail) return;
    if (contactsQuery.isLoading || contactsQuery.isFetching) return;
    if (!contactsQuery.data) return;
    const key = selectedEmail.toLowerCase();
    if (everOnFileRef.current.has(key) && !onFileAddresses.has(key)) {
      onSelect(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- revert on delete
  }, [
    selectedEmail,
    onFileAddresses,
    contactsQuery.isLoading,
    contactsQuery.isFetching,
    contactsQuery.data,
    contactModalOpen,
  ]);

  const openContactModal = React.useCallback(() => {
    knownBeforeAddRef.current = options.map((o) => o.address.toLowerCase());
    setOpen(false);
    setContactModalOpen(true);
  }, [options]);

  const selectedOption = React.useMemo(() => {
    if (!selectedEmail) return null;
    return (
      options.find(
        (o) => o.address.toLowerCase() === selectedEmail.toLowerCase(),
      ) ?? { address: selectedEmail, emailType: selectedEmailType }
    );
  }, [options, selectedEmail, selectedEmailType]);

  const selectedTypeLabel = selectedOption ? typeLabel(selectedOption) : null;

  return (
    <div className="flex w-full items-center gap-3 rounded-xl border border-white/[0.06] bg-[#1f1f20] px-3 py-2.5 shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]">
      <span className="shrink-0 text-sm font-medium text-heading">
        Selected Email Address
      </span>
      <div className="flex-1 min-w-0">
        <Select open={open} onOpenChange={setOpen}>
          <Select.Trigger
            className="h-8 w-full justify-between"
            active={Boolean(selectedEmail)}
          >
            <span className="flex min-w-0 flex-1 items-center gap-2">
              {selectedEmail ? (
                <>
                  <Icon
                    name="IconAt"
                    size={14}
                    className="shrink-0 text-foreground opacity-60"
                  />
                  <span className="truncate text-sm text-foreground">
                    {selectedEmail}
                  </span>
                  <ContactItemDeliverability
                    deliverability={
                      (selectedOption?.deliverability ?? null) as ContactDeliverability
                    }
                    type="email"
                    tooltipClassName="z-[200]"
                  />
                  {selectedTypeLabel ? (
                    <TypePill label={selectedTypeLabel} />
                  ) : null}
                </>
              ) : (
                <span className="truncate text-sm text-destructive">
                  No email selected
                </span>
              )}
            </span>
            <Select.Icon />
          </Select.Trigger>
          <Select.Content
            align="end"
            className="w-[var(--radix-popover-trigger-width)] min-w-[280px]"
          >
            {contactsQuery.isLoading ? (
              <Select.ListPending count={3} />
            ) : (
              <Select.List>
                {options.length === 0 ? (
                  <Select.ListEmpty>No emails on file</Select.ListEmpty>
                ) : (
                  options.map((opt) => {
                    const isSelected =
                      selectedEmail?.toLowerCase() ===
                      opt.address.toLowerCase();
                    const label = typeLabel(opt);
                    const undeliverable = isUndeliverable(opt.deliverability);
                    return (
                      <Select.Item
                        key={opt.address}
                        selected={isSelected}
                        title={
                          undeliverable
                            ? "Marked undeliverable — sending may bounce."
                            : undefined
                        }
                        onSelect={() => {
                          // Undeliverable can be chosen manually (at the
                          // recruiter's risk); it's only excluded from
                          // automatic waterfall selection.
                          onSelect(opt);
                          setOpen(false);
                        }}
                      >
                        <Icon
                          name="IconAt"
                          size={14}
                          className="shrink-0 text-foreground opacity-60"
                        />
                        <Select.ItemLabel selected={isSelected}>
                          {opt.address}
                        </Select.ItemLabel>
                        <ContactItemDeliverability
                          deliverability={
                            (opt.deliverability ?? null) as ContactDeliverability
                          }
                          type="email"
                          tooltipClassName="z-[200]"
                        />
                        {label ? (
                          <span className="shrink-0 text-[11px] text-muted-foreground">
                            {label}
                          </span>
                        ) : null}
                        {isSelected ? <Select.ItemCheck /> : null}
                      </Select.Item>
                    );
                  })
                )}
                <Select.Separator className="my-1" />
                <Select.Item onSelect={openContactModal}>
                  <Icon
                    name="IconPlusMedium"
                    size={14}
                    className="shrink-0 text-foreground opacity-60"
                  />
                  <Select.ItemLabel>Add email address</Select.ItemLabel>
                </Select.Item>
              </Select.List>
            )}
          </Select.Content>
        </Select>
      </div>

      <ContactReviewModal
        profileId={profileId}
        open={contactModalOpen}
        onOpenChange={setContactModalOpen}
        revealAction="enrich"
      />
    </div>
  );
}
